import { useEffect, useRef } from 'react';
import { audioEngine } from '@/lib/audio/AudioEngine';
import type { Phase } from './useTrainingEngine';

/**
 * Wires the training engine phases to the audio engine.
 *
 * Key rule: The countdown sequence OWNS the end-of-work buzzer.
 * If no countdown was scheduled (short work period), the phase transition plays it.
 */
export function useTrainingAudio(phase: Phase, remainingMs: number | null) {
    const prevPhase = useRef<Phase>(phase);
    const countdownScheduled = useRef(false);

    const timerRef = useRef<number | null>(null);

    // Phase transition sounds
    useEffect(() => {
        if (prevPhase.current === phase) return;
        const from = prevPhase.current;
        prevPhase.current = phase;

        // Stop any pending/playing audio (e.g. mid-countdown skip)
        audioEngine.cancelAll();

        // Clear any pending React-side timeouts (e.g. "wrestle" delayed cue)
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (phase === 'work') {
            // New work phase — reset countdown flag for this round
            countdownScheduled.current = false;
            // Play "Work" start sequence: Whistle -> Wrestle
            audioEngine.playCue('whistle');
            timerRef.current = window.setTimeout(() => audioEngine.playCue('wrestle'), 800);
        } else if (phase === 'rest' && from === 'work') {
            // Work → Rest (End of Round)
            // Always play end-of-round sound (Buzzer + Rest), even if skipped manually
            audioEngine.playRoundEnd();
        } else if (phase === 'complete') {
            // Work/Rest → Complete
            audioEngine.playCue('buzzer');
            timerRef.current = window.setTimeout(() => audioEngine.playSessionComplete(), 2000);
        }
    }, [phase]);

    // Schedule countdown at ~6 seconds remaining (work phase only)
    useEffect(() => {
        if (phase !== 'work' || remainingMs === null) return;

        const sec = Math.ceil(remainingMs / 1000);

        // Trigger countdown at 6 seconds so count_5 plays at 5s remaining
        if (sec <= 6 && sec > 0 && !countdownScheduled.current) {
            countdownScheduled.current = true;
            audioEngine.scheduleCountdown(remainingMs);
        }
    }, [remainingMs, phase]);
}

